import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { http, fmtGBP, fmtDate, fmtDateShort } from "@/lib/api";
import { ArrowLeft, MapPin, Storefront, Buildings, Phone, EnvelopeSimple, Wrench, Plus, ImageSquare, Certificate, Clock } from "@phosphor-icons/react";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { PhotoTile } from "@/components/PhotoTile";
import { ComplianceStatusBadge } from "@/pages/Compliance";

const EQ_STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-300",
  under_repair: "bg-amber-50 text-amber-700 border-amber-300",
  retired: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: eq } = useQuery({ queryKey: ["equipment", id], queryFn: () => http.get(`/equipment/${id}`).then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: docTypes = {} } = useQuery({ queryKey: ["compliance-doc-types"], queryFn: () => http.get("/compliance/doc-types").then(r => r.data) });

  if (!eq) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const store = stores.find(s => s.id === eq.store_id);
  const mc = eq.maintenance_company;
  const jobs = eq.job_history || [];
  const docs = eq.compliance_documents || [];
  const openJobs = jobs.filter(j => !["completed", "closed"].includes(j.status));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="equipment-detail-back" className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline">{eq.category || "Equipment"}</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1" data-testid="equipment-detail-name">{eq.name}</h1>
          <div className="flex items-center gap-3 text-sm text-slate-600 mt-1 flex-wrap">
            {store && <Link to={`/stores/${store.id}`} className="inline-flex items-center gap-1 hover:text-[#002FA7]"><Storefront size={12}/> {store.name}</Link>}
            {eq.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {eq.location}</span>}
            <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm ${EQ_STATUS_CLS[eq.status] || EQ_STATUS_CLS.active}`}>{(eq.status || "active").replace("_", " ")}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/jobs/new?equipment_id=${eq.id}`} data-testid="equipment-detail-raise-job" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
            <Plus size={14} weight="bold" /> Raise repair job
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="rmms-card p-4"><div className="overline">Repair history</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{jobs.length}</div></div>
        <div className="rmms-card p-4"><div className="overline">Open jobs</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{openJobs.length}</div></div>
        <div className="rmms-card p-4"><div className="overline">Service records</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{docs.length}</div></div>
        <div className="rmms-card p-4"><div className="overline">Lifetime spend</div><div className="font-mono-data text-2xl mt-2 tabular-nums">{fmtGBP(jobs.reduce((a, j) => a + (j.actual_cost || 0), 0))}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rmms-card p-5">
            <div className="overline mb-3">Asset details</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Manufacturer</dt><dd className="font-mono-data mt-0.5">{eq.manufacturer || "—"}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Model</dt><dd className="font-mono-data mt-0.5">{eq.model || "—"}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Serial</dt><dd className="font-mono-data mt-0.5">{eq.serial_number || "—"}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Installed location</dt><dd className="mt-0.5">{eq.location || "—"}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Storage location</dt><dd className="mt-0.5">{eq.storage_location || "—"}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Purchase date</dt><dd className="font-mono-data mt-0.5">{eq.purchase_date ? fmtDateShort(eq.purchase_date) : "—"}</dd></div>
              <div><dt className="text-[11px] uppercase tracking-wider text-slate-500">Warranty expiry</dt><dd className="font-mono-data mt-0.5">{eq.warranty_expiry ? fmtDateShort(eq.warranty_expiry) : "—"}</dd></div>
              {eq.notes && <div className="sm:col-span-2"><dt className="text-[11px] uppercase tracking-wider text-slate-500">Notes</dt><dd className="mt-0.5 text-slate-700">{eq.notes}</dd></div>}
            </dl>
          </div>

          {/* Repair history */}
          <div className="rmms-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="font-heading font-semibold text-sm flex items-center gap-2"><Wrench size={14}/> Repair history</div>
              <Link to={`/jobs/new?equipment_id=${eq.id}`} className="text-xs text-[#002FA7] hover:underline">+ Raise job</Link>
            </div>
            <div className="divide-y divide-slate-100">
              {jobs.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="equipment-no-jobs">No repair jobs raised for this asset yet.</div>}
              {jobs.map(j => (
                <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`equipment-job-${j.job_number}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-5 py-3 hover:bg-slate-50 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{j.title}</div>
                    <div className="text-[11px] text-slate-500 font-mono-data">{j.job_number} · {fmtDate(j.created_at)}</div>
                  </div>
                  <PriorityBadge priority={j.priority} />
                  <span className="font-mono-data text-sm tabular-nums">{fmtGBP(j.actual_cost || j.estimated_cost)}</span>
                  <StatusBadge status={j.status} />
                </Link>
              ))}
            </div>
          </div>

          {/* Service records / compliance */}
          <div className="rmms-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="font-heading font-semibold text-sm flex items-center gap-2"><Certificate size={14} /> Service records & compliance</div>
            </div>
            <div className="divide-y divide-slate-100">
              {docs.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="equipment-no-docs">No service records or certificates uploaded yet.</div>}
              {docs.map(d => (
                <div key={d.id} data-testid={`equipment-doc-${d.id}`} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-5 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{docTypes[d.doc_type] || d.doc_type}</div>
                    <div className="text-[11px] text-slate-500 truncate">{d.title} {d.issued_by && <>· {d.issued_by}</>}</div>
                  </div>
                  <span className="text-[12px] font-mono-data text-slate-600">{d.expiry_date ? `Exp ${fmtDateShort(d.expiry_date)}` : "—"}</span>
                  <ComplianceStatusBadge status={d.status} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Maintenance company */}
          <div className="rmms-card p-5">
            <div className="overline mb-3 flex items-center gap-1.5"><Buildings size={11} /> Maintenance company</div>
            {mc ? (
              <div className="space-y-2">
                <div className="font-medium">{mc.name}</div>
                {mc.contact_name && <div className="text-xs text-slate-500">Contact: {mc.contact_name}</div>}
                {mc.phone && <div className="text-xs text-slate-600 flex items-center gap-1.5"><Phone size={11} /><span className="font-mono-data">{mc.phone}</span></div>}
                {mc.email && <div className="text-xs text-slate-600 flex items-center gap-1.5"><EnvelopeSimple size={11} /><span className="font-mono-data">{mc.email}</span></div>}
                {mc.contract_notes && <div className="text-[11px] text-slate-500 italic mt-2">{mc.contract_notes}</div>}
                <Link to={`/maintenance-companies`} className="text-xs text-[#002FA7] hover:underline inline-block mt-2">Manage companies →</Link>
              </div>
            ) : (
              <div className="text-sm text-slate-400">No maintenance company linked.
                <Link to={`/equipment?store_id=${eq.store_id}`} className="block mt-2 text-xs text-[#002FA7] hover:underline">Edit equipment to link →</Link>
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="rmms-card p-5">
            <div className="overline mb-3 flex items-center gap-1.5"><ImageSquare size={11} /> Photos</div>
            {eq.photos && eq.photos.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {eq.photos.map(fid => <PhotoTile key={fid} fileId={fid} canRemove={false} />)}
              </div>
            ) : <div className="text-sm text-slate-400">No photos.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
